import { useCallback, useEffect, useState } from "react";
import {
    addComplianceLink,
    deleteComplianceDocument,
    getComplianceDocumentContent,
    listComplianceDocuments,
    renameComplianceDocument,
    uploadComplianceDocument,
} from "../../../services/api";
import { apiErrorMessage, createPreview } from "../model/complianceRules";

// Documents d'un dossier de conformité : chargement, import, liens, aperçu, suppression et renommage.
// Une seule action à la fois par dossier (busy) ; chaque action efface l'erreur précédente.
export function useComplianceFolder(folderKey) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [preview, setPreview] = useState(null);

    const refresh = useCallback(() => {
        setLoading(true);
        setError("");
        listComplianceDocuments(folderKey)
            .then(({ data }) => setDocuments(data || []))
            .catch(loadError => setError(apiErrorMessage(loadError, "Impossible de charger les fichiers.")))
            .finally(() => setLoading(false));
    }, [folderKey]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
    }, [preview?.url]);

    const runAction = async (action, fallback) => {
        setBusy(true);
        setError("");
        try {
            await action();
        } catch (actionError) {
            setError(apiErrorMessage(actionError, fallback));
        } finally {
            setBusy(false);
        }
    };

    const uploadFiles = async (fileList, documentKey = null) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        await runAction(async () => {
            let latest = documents;
            for (const file of files) {
                const response = await uploadComplianceDocument(folderKey, file, documentKey);
                latest = response.data || latest;
            }
            setDocuments(latest);
        }, "L'import du fichier a échoué.");
    };

    const addLink = (url, documentKey = null) => runAction(async () => {
        const response = await addComplianceLink(folderKey, { documentKey, url });
        setDocuments(response.data || []);
    }, "L'ajout du lien a échoué.");

    const openDocument = async (document) => {
        if (document.url) {
            window.open(document.url, "_blank", "noopener,noreferrer");
            return;
        }
        await runAction(async () => {
            const response = await getComplianceDocumentContent(folderKey, document.id);
            setPreview(current => {
                if (current?.url) URL.revokeObjectURL(current.url);
                return createPreview(response, document.fileName);
            });
        }, "Impossible d'ouvrir ce fichier.");
    };

    const removeDocument = (document) => runAction(async () => {
        const response = await deleteComplianceDocument(folderKey, document.id);
        setDocuments(response.data || []);
    }, "Impossible de supprimer ce fichier.");

    const renameDocument = (document, fileName) => runAction(async () => {
        const response = await renameComplianceDocument(folderKey, document.id, fileName);
        setDocuments(response.data || []);
    }, "Impossible de renommer cet élément.");

    const closePreview = () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
    };

    return {
        documents,
        loading,
        busy,
        error,
        preview,
        uploadFiles,
        addLink,
        openDocument,
        removeDocument,
        renameDocument,
        closePreview,
    };
}
